import React, { useCallback, useEffect, useMemo } from 'react';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { WbsLabelValueSearchResult } from 'graphql/types';
import { useLabelName } from 'modules/common/hooks/useLabelName';

import {
  SearchFacetType,
  SuggestionItemProp,
  DetailsControlProps
} from '../type';
import LabelOptionItem from '../OptionItem';
import FacetDetailField from '../FacetDetailsField';
import SearchAutocomplete from '../SearchAutocomplete';
import { mappedLabelValues } from '../LabelFacetDetails';
import OptionItem from '../OptionItem';
import makeCriteriaProvider from './makeCriteriaProvider';
import { billingTag } from './tag';
import { options } from './util';

export const getDetailsControl = (
  label: TimesheetLabel,
  onDropdownValueChange?: (mappedValue: any, label: any) => void,
  isDisable?: (label: any) => boolean,
  settingDopDownValue?: (label: any, setSelected: any) => void
): React.FC<DetailsControlProps> =>
  // eslint-disable-next-line react/display-name
  ({ selected, setSelected }: DetailsControlProps) => {
    const labelName = useLabelName(label);
    useEffect(() => {
      if (settingDopDownValue) {
        settingDopDownValue(label, setSelected);
      }
    }, [setSelected]);
    const onValueChange = useCallback(
      (selectedOptions) => {
        const mapValues = mappedLabelValues(selectedOptions, label.id);
        setSelected(mapValues);

        if (onDropdownValueChange) {
          onDropdownValueChange(mapValues, label);
        }
      },
      [setSelected]
    );

    const getOptionDisabled = useCallback(
      (option: WbsLabelValueSearchResult) => {
        return selected.some((v) => v.id === option.id);
      },
      [selected]
    );

    const getOptionSelected = useCallback(
      (option: WbsLabelValueSearchResult, value: WbsLabelValueSearchResult) => {
        return option.displayText === value.displayText;
      },
      []
    );

    const getOptionLabel = useCallback(
      (option: WbsLabelValueSearchResult) =>
        (option && option.displayText) ?? '',
      []
    );

    const disable = useMemo(() => {
      if (isDisable) {
        return isDisable(label);
      }
      return false;
    }, []);
    const renderOption = useCallback(
      (props: any, option: WbsLabelValueSearchResult) => {
        return (
          <li {...props}>
            <OptionItem value={option.displayText} />
          </li>
        );
      },
      []
    );

    return (
      <FacetDetailField label={labelName}>
        <SearchAutocomplete
          dataTestId={`search-auto-complete-${label.type.toLowerCase()}`}
          options={options}
          value={selected}
          onChange={onValueChange}
          getOptionDisabled={getOptionDisabled}
          getOptionSelected={getOptionSelected}
          getOptionLabel={getOptionLabel}
          renderOption={renderOption}
          variant="standard"
          hiddenLabel
          loading={false}
          disabled={disable}
        />
      </FacetDetailField>
    );
  };

const SuggestionItem: React.FC<SuggestionItemProp> = ({
  option
}: SuggestionItemProp) => (
  <LabelOptionItem testId={option.id} value={option.label} />
);

export const getBillingFacet = (
  label: TimesheetLabel,
  onDropdownValueChange?: (mappedValue: any, label: any) => void,
  isDisable?: (label: any) => boolean,
  settingDopDownValue?: (label: any, setSelected: any) => void
): SearchFacetType => ({
  makeCriteriaProvider: makeCriteriaProvider(label),
  tag: billingTag,
  DetailsControl: getDetailsControl(
    label,
    onDropdownValueChange,
    isDisable,
    settingDopDownValue
  ),
  SuggestionItem: SuggestionItem
});

export default getBillingFacet;
